import { useConfirmation } from '@/hooks/useConfirmation';
// Phase node-extension-compiler-ui (TODO-048 Part B)
//
// CRUD manager for per-submission node extensions. Shown in SubmissionDetailPage
// under the "Node Extensions" tab. FDA market: read-only notice. Other markets:
// full add / edit / delete UI.

import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, type NodeExtension } from '@/api/v2Client';
import { describeApiError } from '@/lib/apiErrorView';
import { normalizeNodeExtensionCode } from '@/lib/nodeExtensionCode';

export interface NodeExtensionManagerProps {
  readonly submissionId: string;
  /** Submission region (e.g. "FDA", "Health Canada", "EMA"). */
  readonly region: string;
}

const sectionStyle: CSSProperties = { padding: 20 };

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
  fontSize: 13,
};

const codeStyle: CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  color: 'var(--accent)',
  minWidth: 80,
};

const labelStyle: CSSProperties = { flex: 1, color: 'var(--text)' };

const sectionCodeStyle: CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  color: 'var(--text-muted)',
  minWidth: 70,
};

const btnSmall: CSSProperties = {
  padding: '3px 10px',
  fontSize: 12,
  border: '1px solid var(--border)',
  borderRadius: 4,
  cursor: 'pointer',
  background: '#fff',
  color: 'var(--text)',
};

const btnDanger: CSSProperties = { ...btnSmall, color: 'var(--red)', borderColor: 'var(--red)' };

const inputStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  marginTop: 4,
};

interface FormState {
  SectionCode: string;
  ExtensionCode: string;
  ExtensionLabel: string;
}

const EMPTY_FORM: FormState = { SectionCode: '', ExtensionCode: '', ExtensionLabel: '' };

export function NodeExtensionManager({
  submissionId,
  region,
}: NodeExtensionManagerProps): ReactElement {
  const { ask, dialog } = useConfirmation();
  const isFda = region === 'FDA';

  const [extensions, setExtensions] = useState<NodeExtension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<FormState>(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2.nodeExtensions.list(submissionId);
      setExtensions(res);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load node extensions').message);
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    if (!isFda) void load();
  }, [load, isFda]);

  const REQUIRED_MSG = 'Section code, extension code, and label are all required.';

  const handleAdd = async (): Promise<void> => {
    const SectionCode = normalizeNodeExtensionCode(addForm.SectionCode);
    const ExtensionCode = normalizeNodeExtensionCode(addForm.ExtensionCode);
    const ExtensionLabel = addForm.ExtensionLabel.trim();
    if (SectionCode === '' || ExtensionCode === '' || ExtensionLabel === '') {
      setAddError(REQUIRED_MSG);
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      await v2.nodeExtensions.create(submissionId, { SectionCode, ExtensionCode, ExtensionLabel });
      setAddForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (err) {
      setAddError(describeApiError(err, 'Failed to add node extension').message);
    } finally {
      setAddSaving(false);
    }
  };

  const openEdit = (ext: NodeExtension): void => {
    setEditId(ext.Id);
    setEditForm({
      SectionCode: ext.SectionCode,
      ExtensionCode: ext.ExtensionCode,
      ExtensionLabel: ext.ExtensionLabel,
    });
    setEditError(null);
  };

  const handleEdit = async (): Promise<void> => {
    if (!editId) return;
    const SectionCode = normalizeNodeExtensionCode(editForm.SectionCode);
    const ExtensionCode = normalizeNodeExtensionCode(editForm.ExtensionCode);
    const ExtensionLabel = editForm.ExtensionLabel.trim();
    if (SectionCode === '' || ExtensionCode === '' || ExtensionLabel === '') {
      setEditError(REQUIRED_MSG);
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await v2.nodeExtensions.update(submissionId, editId, { SectionCode, ExtensionCode, ExtensionLabel });
      setEditId(null);
      await load();
    } catch (err) {
      setEditError(describeApiError(err, 'Failed to update node extension').message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (extId: string): Promise<void> => {
    if (!(await ask({ title: 'Delete node extension', message: 'Delete this node extension? Documents linked to it will be unlinked.', confirmLabel: 'Delete', variant: 'destructive' }))) return;
    setDeletingId(extId);
    try {
      await v2.nodeExtensions.delete(submissionId, extId);
      await load();
    } catch (err) {
      setError(describeApiError(err, 'Failed to delete node extension').message);
    } finally {
      setDeletingId(null);
    }
  };

  if (isFda) {
    return (
      <div data-testid="node-ext-fda-notice" style={{ ...sectionStyle, color: 'var(--text-muted)', fontSize: 13 }}>
        Node extensions are not permitted in FDA submissions. The compiler will abort if any are
        present. No extensions can be added to this submission.
      </div>
    );
  }

  return (
    <div data-testid="node-ext-manager" style={sectionStyle}>
      {dialog}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Node Extensions</h3>
        <button
          type="button"
          data-testid="node-ext-add-btn"
          onClick={() => { setAddOpen(true); setAddError(null); }}
          style={{
            padding: '6px 14px',
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Add node extension
        </button>
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}
      {error && <div role="alert" style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}

      {!loading && !error && extensions.length === 0 && (
        <div data-testid="node-ext-empty" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {/* phase-fix-submission-details-polish (review §2 #15) — the empty
              state used to read HC-specific guidance on every region. Copy is
              now region-aware, mirroring the backend node-extension policy
              (submission-node-extensions.ts): HC restricted to specific
              parents, PMDA discouraged, EMA/MHRA/TGA/Swissmedic permitted
              (FDA never sees this tab — blocked outright). */}
          {region === 'Health Canada'
            ? 'No node extensions defined. For Health Canada submissions, node extensions are permitted under section 1.2.6 (Authorization for Sharing Information — title with the company granted access) and section 1.2.7 (International Information — title with the agency name, e.g. EMA, FDA, TGA, WHO). Enter the full section path: m1/1.2.6 or m1/1.2.7.'
            : region === 'PMDA'
              ? 'No node extensions defined. PMDA discourages node extensions — prefer the standard CTD headings; extensions added here will be flagged by validation.'
              : `No node extensions defined. ${region || 'This region'} permits node extensions for custom sub-headings beyond the standard CTD structure; they are checked against the region's eCTD policy at validation.`}
        </div>
      )}

      {extensions.map((ext) => (
        editId === ext.Id ? (
          <div key={ext.Id} data-testid={`node-ext-edit-${ext.Id}`}
            style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <p data-testid="node-ext-format-help-edit"
              style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
              Section and extension codes use the canonical module-prefixed path format,
              e.g. m1/1.2.7. A bare number like 1.2.7 is normalized to m1/1.2.7 on save.
            </p>
            {(['SectionCode', 'ExtensionCode', 'ExtensionLabel'] as const).map((field) => (
              <label key={field} style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                {field}
                <input
                  type="text"
                  placeholder={field === 'SectionCode' ? 'e.g. m1/1.2.7' : undefined}
                  value={editForm[field]}
                  onChange={(e) => setEditForm((f) => ({ ...f, [field]: e.target.value }))}
                  style={inputStyle}
                  data-testid={`node-ext-edit-${field.toLowerCase()}`}
                />
              </label>
            ))}
            {editError && <div style={{ color: 'var(--red)', fontSize: 12 }}>{editError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={() => void handleEdit()} disabled={editSaving}
                style={{ ...btnSmall, background: 'var(--accent)', color: '#fff', border: 'none' }}
                data-testid="node-ext-edit-save">
                {editSaving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditId(null)} style={btnSmall}
                data-testid="node-ext-edit-cancel">Cancel</button>
            </div>
          </div>
        ) : (
          <div key={ext.Id} data-testid={`node-ext-row-${ext.Id}`} style={rowStyle}>
            <span style={codeStyle}>{ext.ExtensionCode}</span>
            <span style={sectionCodeStyle}>({ext.SectionCode})</span>
            <span style={labelStyle}>{ext.ExtensionLabel}</span>
            <button type="button" onClick={() => openEdit(ext)} style={btnSmall}
              data-testid={`node-ext-edit-btn-${ext.Id}`}>Edit</button>
            <button type="button" onClick={() => void handleDelete(ext.Id)}
              disabled={deletingId === ext.Id} style={btnDanger}
              data-testid={`node-ext-delete-btn-${ext.Id}`}>
              {deletingId === ext.Id ? '…' : 'Delete'}
            </button>
          </div>
        )
      ))}

      {addOpen && (
        <div data-testid="node-ext-add-form"
          style={{ marginTop: 16, padding: 16, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6 }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700 }}>Add node extension</h4>
          <p data-testid="node-ext-format-help-add"
            style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
            Section and extension codes use the canonical module-prefixed path format,
            e.g. m1/1.2.7. A bare number like 1.2.7 is normalized to m1/1.2.7 on save.
          </p>
          {(['SectionCode', 'ExtensionCode', 'ExtensionLabel'] as const).map((field) => (
            <label key={field} style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
              {field}
              <input
                type="text"
                placeholder={field === 'SectionCode' ? 'e.g. m1/1.2.7' : undefined}
                value={addForm[field]}
                onChange={(e) => setAddForm((f) => ({ ...f, [field]: e.target.value }))}
                style={inputStyle}
                data-testid={`node-ext-add-${field.toLowerCase()}`}
              />
            </label>
          ))}
          {addError && <div style={{ color: 'var(--red)', fontSize: 12 }}>{addError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => void handleAdd()} disabled={addSaving}
              style={{ ...btnSmall, background: 'var(--accent)', color: '#fff', border: 'none' }}
              data-testid="node-ext-add-save">
              {addSaving ? 'Saving…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setAddOpen(false); setAddForm(EMPTY_FORM); }}
              style={btnSmall} data-testid="node-ext-add-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
